import { FC } from 'react';
import NumberFlow from '@number-flow/react';
import { Button, Space } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { MiddleDot } from '@/shared';
import { InlineFooterSkeleton } from '@/shared/components/skeletons';
import { TbChevronLeft, TbChevronRight } from '@/vendor/react-icons-tb';

import type { SearchResultsFooterSnapshot } from '../../SearchResultsFooterState';

interface SearchResultsFooterProps {
    snapshot: SearchResultsFooterSnapshot | null;
}

const getElapsedTimePresentation = (elapsedMs: number) => {
    if (elapsedMs < 1_000) {
        return {
            format: { maximumFractionDigits: 0, useGrouping: true },
            suffix: 'ms',
            value: Math.max(0, Math.round(elapsedMs)),
        };
    }

    const fractionDigits = elapsedMs < 10_000 ? 2 : 1;

    return {
        format: { maximumFractionDigits: fractionDigits, minimumFractionDigits: fractionDigits },
        suffix: 's',
        value: elapsedMs / 1_000,
    };
};

const SearchResultsFooter: FC<SearchResultsFooterProps> = ({ snapshot }): JSX.Element | null => {
    const { token } = useAntDesignTheme();

    if (!snapshot) {
        return null;
    }

    const initialLoading = snapshot.loading && snapshot.elapsedMs === undefined;
    const totalPages = Math.max(1, Math.ceil(snapshot.total / Math.max(1, snapshot.pageSize)));
    const page = Math.min(snapshot.page, totalPages);
    const paginationAvailable = Boolean(snapshot.onPageChange) && totalPages > 1;
    const elapsedTime = snapshot.elapsedMs === undefined ? null : getElapsedTimePresentation(snapshot.elapsedMs);

    return (
        <small
            aria-busy={snapshot.loading}
            style={{
                alignItems: 'center',
                color: token.colorTextTertiary,
                display: 'inline-flex',
                height: '1rem',
                lineHeight: '1rem',
                whiteSpace: 'nowrap',
            }}
        >
            {initialLoading ? (
                <InlineFooterSkeleton width='5rem' />
            ) : (
                <span>
                    <NumberFlow
                        format={{ maximumFractionDigits: 0, useGrouping: true }}
                        value={snapshot.total}
                        willChange
                    />{' '}
                    Total Results
                </span>
            )}
            <MiddleDot horizontalMargin='0.5rem' />
            {initialLoading || !elapsedTime ? (
                <InlineFooterSkeleton width='4.5rem' />
            ) : (
                <span>
                    Returned in{' '}
                    <NumberFlow
                        format={elapsedTime.format}
                        suffix={elapsedTime.suffix}
                        value={elapsedTime.value}
                        willChange
                    />
                </span>
            )}
            <MiddleDot horizontalMargin='0.5rem' />
            {initialLoading ? (
                <InlineFooterSkeleton width='3.5rem' />
            ) : (
                <span>
                    Page <NumberFlow format={{ maximumFractionDigits: 0, useGrouping: true }} value={page} willChange />{' '}
                    of{' '}
                    <NumberFlow
                        format={{ maximumFractionDigits: 0, useGrouping: true }}
                        value={totalPages}
                        willChange
                    />
                </span>
            )}
            {paginationAvailable && !initialLoading ? (
                <>
                    <MiddleDot horizontalMargin='0.5rem' />
                    <Space.Compact size='small' style={{ alignItems: 'center', display: 'inline-flex' }}>
                        <Button
                            aria-label='Previous search results page'
                            disabled={snapshot.loading || page <= 1}
                            icon={<TbChevronLeft size='0.75rem' />}
                            onClick={() => snapshot.onPageChange?.(page - 1)}
                            size='small'
                            style={{
                                alignItems: 'center',
                                display: 'inline-flex',
                                justifyContent: 'center',
                            }}
                            type='text'
                        />
                        <Button
                            aria-label='Next search results page'
                            disabled={snapshot.loading || page >= totalPages}
                            icon={<TbChevronRight size='0.75rem' />}
                            onClick={() => snapshot.onPageChange?.(page + 1)}
                            size='small'
                            style={{
                                alignItems: 'center',
                                display: 'inline-flex',
                                justifyContent: 'center',
                            }}
                            type='text'
                        />
                    </Space.Compact>
                </>
            ) : null}
        </small>
    );
};

export default SearchResultsFooter;
